import { useMemo, useState } from 'react'
import {
  FileStack,
  Download,
  Layers,
  ShieldCheck,
  Menu,
  PanelRight,
  Undo2,
  Redo2,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useProjectStore, deriveOrderedPages } from '../store/projectStore.js'
import { exportAndDownload } from '../lib/pdfActions.js'

const THEMES = [
  { id: 'light', icon: Sun },
  { id: 'dark', icon: Moon },
  { id: 'system', icon: Monitor },
]

export default function TopBar({
  onMenuClick,
  onInspectorClick,
  leftOpen,
  rightOpen,
}) {
  const documents = useProjectStore((s) => s.documents)
  const pageOrder = useProjectStore((s) => s.pageOrder)
  const exportOptions = useProjectStore((s) => s.exportOptions)
  const signatures = useProjectStore((s) => s.signatures)
  const splitGroups = useProjectStore((s) => s.splitGroups)
  const setProcessing = useProjectStore((s) => s.setProcessing)
  const showToast = useProjectStore((s) => s.showToast)
  const undo = useProjectStore((s) => s.undo)
  const redo = useProjectStore((s) => s.redo)
  const historyPast = useProjectStore((s) => s.history.past.length)
  const historyFuture = useProjectStore((s) => s.history.future.length)
  const theme = useProjectStore((s) => s.theme)
  const setTheme = useProjectStore((s) => s.setTheme)

  const pages = useMemo(
    () => deriveOrderedPages(documents, pageOrder),
    [documents, pageOrder]
  )
  const includedCount = pages.filter((p) => p.included).length
  const totalCount = pages.length

  const [themeOpen, setThemeOpen] = useState(false)
  const currentTheme = THEMES.find((t) => t.id === theme) || THEMES[2]
  const ThemeIcon = currentTheme.icon

  const handleExport = async () => {
    if (includedCount === 0) {
      showToast('Enable at least one page to export.', 'warning')
      return
    }
    try {
      setProcessing(true, exportOptions.mode === 'images'
        ? 'Rendering pages as images…'
        : exportOptions.mode === 'split'
        ? 'Building split PDFs…'
        : exportOptions.compression === 'rasterize'
        ? 'Rasterizing & compressing PDF…'
        : 'Building your PDF…'
      )
      const name =
        documents.length === 1
          ? `workbench-${documents[0].originalName}`
          : `workbench-export-${new Date().toISOString().slice(0, 10)}.pdf`
      const fixedName = name.endsWith('.pdf') ? name : `${name}.pdf`
      const result = await exportAndDownload({
        documents,
        pageOrder,
        exportOptions,
        signatures,
        splitGroups,
        filename: fixedName,
        onProgress: (p) => {
          if (p && p.total) {
            setProcessing(true, `${p.name || 'Processing'} ${p.index + 1}/${p.total}…`)
          }
        },
      })
      const summary =
        exportOptions.mode === 'images'
          ? `Exported ${result.count} image${result.count === 1 ? '' : 's'}.`
          : exportOptions.mode === 'split'
          ? `Exported ${result.count} PDF${result.count === 1 ? '' : 's'} as a zip.`
          : `Exported ${includedCount} page${includedCount === 1 ? '' : 's'}.`
      showToast(summary, 'success')
    } catch (err) {
      console.error(err)
      showToast(err.message || 'Export failed.', 'error')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <header className="flex h-[57px] items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-3 shadow-soft sm:px-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={leftOpen ? 'Close documents panel' : 'Open documents panel'}
          aria-expanded={leftOpen}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white shadow-soft">
            <Layers className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold tracking-tight text-slate-900 sm:text-base dark:text-slate-100">
              Pro PDF Workbench
            </h1>
            <p className="hidden truncate text-xs text-slate-500 sm:block dark:text-slate-400">
              Merge, redact, OCR, sign, watermark — all in your browser.
            </p>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <div className="hidden items-center gap-1 rounded-lg bg-slate-100 p-0.5 sm:flex dark:bg-slate-800">
          <button
            type="button"
            onClick={undo}
            disabled={historyPast === 0}
            className="rounded-md p-1.5 text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
            title="Undo (Cmd/Ctrl+Z)"
          >
            <Undo2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={historyFuture === 0}
            className="rounded-md p-1.5 text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
            title="Redo (Cmd/Ctrl+Shift+Z)"
          >
            <Redo2 className="h-4 w-4" />
          </button>
        </div>

        <div className="relative hidden sm:block">
          <button
            type="button"
            onClick={() => setThemeOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            title="Theme"
            aria-label="Change theme"
          >
            <ThemeIcon className="h-4 w-4" />
          </button>
          {themeOpen && (
            <div
              className="absolute right-0 top-full z-40 mt-1 w-32 rounded-lg border border-slate-200 bg-white p-1 shadow-lift dark:border-slate-700 dark:bg-slate-800"
              onMouseLeave={() => setThemeOpen(false)}
            >
              {THEMES.map((t) => {
                const Icon = t.icon
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTheme(t.id)
                      setThemeOpen(false)
                    }}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs capitalize ${
                      theme === t.id
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {t.id}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="hidden items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 lg:flex dark:bg-emerald-900/30 dark:text-emerald-300">
          <ShieldCheck className="h-3.5 w-3.5" />
          Client-side
        </div>
        <div className="hidden items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 md:flex dark:bg-slate-800 dark:text-slate-300">
          <FileStack className="h-3.5 w-3.5" />
          {documents.length} doc{documents.length === 1 ? '' : 's'} · {includedCount}/{totalCount} pages
        </div>
        <button
          onClick={handleExport}
          disabled={totalCount === 0}
          className="btn-primary h-9 px-3 text-xs sm:px-4 sm:text-sm"
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">
            Export {exportOptions.mode === 'images' ? 'images' : exportOptions.mode === 'split' ? 'split' : 'PDF'}
          </span>
          <span className="sm:hidden">Export</span>
        </button>
        <button
          type="button"
          onClick={onInspectorClick}
          aria-label={rightOpen ? 'Close inspector' : 'Open inspector'}
          aria-expanded={rightOpen}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <PanelRight className="h-5 w-5" />
        </button>
      </div>
    </header>
  )
}
